import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { wsService } from "../services/websocket";
import { formatDistanceToNow } from "date-fns";
import {
  PlayIcon,
  StopIcon,
  CheckIcon,
  ArrowLeftIcon,
  ChartBarIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  UserGroupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../components/LoadingSpinner";
import Footer from "../components/Footer";
import Toggle from "../components/Toggle";
import toast from "react-hot-toast";
import { useSessionStore } from "../store/sessionStore";

const SessionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const {
    sessions,
    chatters,
    stats,
    isLoading,
    fetchSessions,
    updateSession,
    setCurrentSession,
    toggleChatterGreeted,
    fetchChatters,
    addChatter,
    updateChatter,
  } = useSessionStore();

  const [session, setSession] = useState(sessions.find((s) => s.id === id));
  const [recentlyGreeted, setRecentlyGreeted] = useState<Set<string>>(
    new Set()
  );
  const [hideGreeted, setHideGreeted] = useState(() => {
    // Initialize from localStorage, default to false
    const saved = localStorage.getItem("hideGreeted");
    return saved ? JSON.parse(saved) : false;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Helper functions to handle session keywords as JSON string
  const parseSessionKeywords = (jsonString: string): string[] => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  };

  // Helper function to check if current user is session owner
  const isSessionOwner = (session: any) => {
    return session.userId === user?.id;
  };

  // Calculate pagination
  const filteredChatters = chatters
    .filter(
      (chatter) =>
        !hideGreeted || !chatter.isGreeted || recentlyGreeted.has(chatter.id)
    )
    .sort(
      (a, b) =>
        new Date(a.firstSeenAt).getTime() - new Date(b.firstSeenAt).getTime()
    );

  const totalPages = Math.ceil(filteredChatters.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedChatters = filteredChatters.slice(startIndex, endIndex);

  // Save hideGreeted to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("hideGreeted", JSON.stringify(hideGreeted));
  }, [hideGreeted]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [hideGreeted, pageSize]);

  useEffect(() => {
    if (!sessions.length) {
      fetchSessions();
    }
  }, [fetchSessions, sessions.length]);

  useEffect(() => {
    const foundSession = sessions.find((s) => s.id === id);
    setSession(foundSession);
    if (foundSession) {
      setCurrentSession(foundSession);
      fetchChatters(foundSession.id);

      // Join the session to receive real-time updates
      wsService.joinSession(foundSession.id);
    }
  }, [sessions, id, setCurrentSession, fetchChatters]);

  useEffect(() => {
    // WebSocket event listeners
    const handleNewChatter = (chatter: any) => {
      addChatter(chatter);
      toast.success(`New chatter: ${chatter.displayName}`, { duration: 3000 });
    };

    const handleChatterUpdate = (chatter: any) => {
      updateChatter(chatter);
    };

    wsService.on("new_chatter", handleNewChatter);
    wsService.on("chatter_update", handleChatterUpdate);

    return () => {
      wsService.off("new_chatter", handleNewChatter);
      wsService.off("chatter_update", handleChatterUpdate);
    };
  }, [addChatter, updateChatter]);

  const handleStartSession = async () => {
    if (!session || !user) return;

    try {
      await updateSession(session.id, { isActive: true });
      wsService.startSession(
        session.id,
        parseSessionKeywords(session.keywords)
      );
      toast.success(`Session started! Monitoring ${session.user.username}'s chat`);
    } catch (error: any) {
      if (error.response?.data?.error === 'Only session owners can start sessions') {
        toast.error('Only session owners can start sessions. Editors can view and manage chatters.');
      } else {
        toast.error("Failed to start session");
      }
    }
  };

  const handleStopSession = async () => {
    if (!session) return;

    try {
      await updateSession(session.id, { isActive: false });
      wsService.stopSession();
      toast.success("Session stopped!");
    } catch (error) {
      toast.error("Failed to stop session");
    }
  };

  const handleToggleGreet = async (chatterId: string, isGreeted: boolean) => {
    if (isGreeted) {
      // Add to recently greeted for animation
      setRecentlyGreeted((prev) => new Set(prev).add(chatterId));

      // Remove from recently greeted after 2 seconds
      setTimeout(() => {
        setRecentlyGreeted((prev) => {
          const newSet = new Set(prev);
          newSet.delete(chatterId);
          return newSet;
        });
      }, 2000);
    }

    await toggleChatterGreeted(chatterId, isGreeted);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">
            Session Not Found
          </h1>
          <p className="text-gray-400 mb-6">
            The session you're looking for doesn't exist.
          </p>
          <Link to="/dashboard" className="btn-primary">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="px-6 lg:px-12 py-8 flex-1">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link
              to="/dashboard"
              className="p-2 text-gray-400 hover:text-white transition-colors"
              title="Back to Dashboard"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-white light:text-gray-900">
                {session.name}
              </h1>
              <p className="text-gray-400 light:text-gray-600">
                Created{" "}
                {formatDistanceToNow(new Date(session.createdAt), {
                  addSuffix: true,
                })}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <span
              className={`px-3 py-1 rounded-full text-sm ${
                session.isActive
                  ? "bg-green-900 text-green-300"
                  : "bg-gray-700 text-gray-300"
              }`}
            >
              {session.isActive ? "Active" : "Inactive"}
            </span>

            {/* Only show start/stop buttons for session owners */}
            {isSessionOwner(session) && (
              session.isActive ? (
                <button
                  onClick={handleStopSession}
                  className="flex items-center px-4 py-2 bg-gray-700 border border-red-400 text-red-400 font-medium rounded-lg transition-colors duration-200 hover:bg-red-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                >
                  <StopIcon className="h-4 w-4 mr-2" />
                  Stop Session
                </button>
              ) : (
                <button onClick={handleStartSession} className="btn-primary">
                  <PlayIcon className="h-4 w-4 mr-2" />
                  Start Session
                </button>
              )
            )}
          </div>
        </div>

        {/* Keywords */}
        {parseSessionKeywords(session.keywords).length > 0 && (
          <div className="card p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-400 light:text-gray-600 mb-2">
              Keywords
            </h3>
            <div className="flex flex-wrap gap-2">
              {parseSessionKeywords(session.keywords).map((keyword) => (
                <span
                  key={keyword}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-twitch-600 text-white"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-8 w-8 text-twitch-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">
                    Total Chatters
                  </dt>
                  <dd className="text-lg font-medium text-white light:text-gray-900">
                    {stats.total}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">
                    Greeted
                  </dt>
                  <dd className="text-lg font-medium text-white light:text-gray-900">
                    {stats.greeted}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">
                    Pending
                  </dt>
                  <dd className="text-lg font-medium text-white light:text-gray-900">
                    {stats.pending}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-400 light:text-gray-600 truncate">
                    Progress
                  </dt>
                  <dd className="text-lg font-medium text-white light:text-gray-900">
                    {stats.greetedPercentage}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Chatters */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white light:text-gray-900">
              People To Greet ({filteredChatters.length})
            </h2>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm">
                <label className="text-gray-300 light:text-gray-700">
                  Show:
                </label>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="bg-gray-700 light:bg-white border border-gray-600 light:border-gray-300 rounded px-2 py-1 text-white light:text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-twitch-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={40}>40</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <label className="flex items-center space-x-2 text-sm">
                <Toggle
                  checked={hideGreeted}
                  onChange={(checked) => setHideGreeted(checked)}
                  size="sm"
                />
                <span className="text-gray-300 light:text-gray-700">
                  Hide greeted
                </span>
              </label>
            </div>
          </div>

          {filteredChatters.length > 0 ? (
            <>
              <div className="space-y-4">
                {paginatedChatters.map((chatter) => (
                  <div key={chatter.id} className="chatter-item">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center space-x-4 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-600 light:bg-gray-300 flex items-center justify-center">
                            {chatter.profileImage ? (
                              <img
                                src={chatter.profileImage}
                                alt={chatter.displayName}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span className="text-lg font-medium text-white light:text-gray-700">
                                {chatter.displayName.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-lg font-medium text-white light:text-gray-900 truncate">
                            {chatter.displayName}
                          </p>
                          <p className="text-sm text-gray-400 light:text-gray-600 truncate">
                            "{chatter.firstMessage}"
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500 light:text-gray-600 mt-1">
                            <span>
                              First seen{" "}
                              {formatDistanceToNow(
                                new Date(chatter.firstSeenAt),
                                { addSuffix: true }
                              )}
                            </span>
                            <span>•</span>
                            {/* {chatter.messageCount > 1 && (
                              <span>• {chatter.messageCount} messages</span>
                            )} */}
                            {chatter.isGreeted && chatter.greetedAt && (
                              <span className="inline-flex items-center gap-x-1 text-sm">
                                Greeted{" "}
                                {formatDistanceToNow(
                                  new Date(chatter.greetedAt),
                                  { addSuffix: true }
                                )}
                                {chatter.greetedByUser && (
                                  <>
                                    <span className="text-gray-400 light:text-gray-600">
                                      by
                                    </span>
                                    {chatter.greetedByUser.profileImage ? (
                                      <img
                                        src={chatter.greetedByUser.profileImage}
                                        alt={chatter.greetedByUser.displayName}
                                        className="w-4 h-4 rounded-full"
                                      />
                                    ) : (
                                      <div className="w-4 h-4 rounded-full bg-twitch-600 flex items-center justify-center">
                                        <span className="text-xs text-white font-medium">
                                          {chatter.greetedByUser.displayName
                                            .charAt(0)
                                            .toUpperCase()}
                                        </span>
                                      </div>
                                    )}
                                    <span className="text-twitch-400 font-medium">
                                      {chatter.greetedByUser.displayName}
                                    </span>
                                  </>
                                )}
                              </span>
                            )}
                            {chatter.isGreeted &&
                              chatter.greetedByUser &&
                              !chatter.greetedAt && (
                                <span className="inline-flex items-center gap-x-1 text-sm">
                                  Greeted by
                                  {chatter.greetedByUser.profileImage ? (
                                    <img
                                      src={chatter.greetedByUser.profileImage}
                                      alt={chatter.greetedByUser.displayName}
                                      className="w-4 h-4 rounded-full"
                                    />
                                  ) : (
                                    <div className="w-4 h-4 rounded-full bg-twitch-600 flex items-center justify-center">
                                      <span className="text-xs text-white font-medium">
                                        {chatter.greetedByUser.displayName
                                          .charAt(0)
                                          .toUpperCase()}
                                      </span>
                                    </div>
                                  )}
                                  <span className="text-twitch-400 font-medium">
                                    {chatter.greetedByUser.displayName}
                                  </span>
                                </span>
                              )}
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-6">
                        <button
                          onClick={() =>
                            handleToggleGreet(chatter.id, !chatter.isGreeted)
                          }
                          className={`w-12 h-12 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${
                            chatter.isGreeted
                              ? recentlyGreeted.has(chatter.id)
                                ? "bg-blue-500 text-white hover:bg-blue-600 opacity-100"
                                : "bg-green-600 text-white hover:bg-green-700"
                              : "bg-gray-600 text-gray-300 hover:bg-gray-500 hover:text-white"
                          }`}
                        >
                          {chatter.isGreeted ? (
                            recentlyGreeted.has(chatter.id) ? (
                              <span
                                className="inline-block"
                                style={{
                                  animation: "wave 0.8s ease-in-out infinite",
                                  transformOrigin: "bottom center",
                                }}
                              >
                                👋
                              </span>
                            ) : (
                              <span>😊</span>
                            )
                          ) : (
                            <span>⏳</span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-700 light:border-gray-200">
                  <div className="text-sm text-gray-400 light:text-gray-600">
                    Showing {startIndex + 1}-
                    {Math.min(endIndex, filteredChatters.length)} of{" "}
                    {filteredChatters.length} chatters
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-600 light:border-gray-300 text-gray-400 light:text-gray-600 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(
                          (page) =>
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 2 && page <= currentPage + 2)
                        )
                        .map((page, index, array) => (
                          <React.Fragment key={page}>
                            {index > 0 && array[index - 1] !== page - 1 && (
                              <span className="text-gray-400 light:text-gray-600 px-2">
                                ...
                              </span>
                            )}
                            <button
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                                currentPage === page
                                  ? "bg-twitch-600 text-white"
                                  : "text-gray-400 light:text-gray-600 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-100"
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-600 light:border-gray-300 text-gray-400 light:text-gray-600 hover:text-white light:hover:text-gray-900 hover:bg-gray-700 light:hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-400 py-12">
              <ChatBubbleLeftIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium text-white light:text-gray-900 mb-2">
                {chatters.length === 0
                  ? "No chatters yet"
                  : "No chatters to show"}
              </h3>
              <p className="text-gray-400 light:text-gray-600 mb-6">
                {chatters.length === 0
                  ? session.isActive
                    ? "Waiting for people to chat in your stream..."
                    : "Start the session to begin monitoring your chat!"
                  : 'All chatters are hidden. Toggle "Hide greeted" to see them.'}
              </p>
              {!session.isActive && chatters.length === 0 && (
                <button onClick={handleStartSession} className="btn-primary">
                  <PlayIcon className="h-4 w-4 mr-2" />
                  Start Session
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes wave {
          0%, 100% { transform: rotate(-15deg); }
          50% { transform: rotate(15deg); }
        }
      `}</style>

      <Footer />
    </>
  );
};

export default SessionPage;
